import { useCallback, useMemo } from "react";
import { isEmpty } from "lodash";

import type { TreeNodeData } from "src/shared/TreeViewNext";
import { LOAD_MORE_OBJECTIVES_NODE_ID } from "src/components/GoalsExplore/utils/objectivesTree.util";

export type DisplayTreeObjectiveNode = TreeNodeData & {
  objective?: Record<string, unknown> | undefined;
  group?: Record<string, unknown> | undefined;
  toggled?: boolean | undefined;
  isLoadMore?: boolean | undefined;
  noActivation?: boolean | undefined;
  active?: boolean | undefined;
};

type UseDisplayTreeDataParams = {
  treeData: DisplayTreeObjectiveNode[];
  hasMore: boolean;
  loadingMore: boolean;
  handleLoadMore: (args: { includeChildObjectivesParam: boolean }) => void;
  totalFetchedCount: number;
  totalCount: number;
};

const useDisplayTreeData = ({
  treeData,
  hasMore,
  loadingMore,
  handleLoadMore,
  totalFetchedCount,
  totalCount,
}: UseDisplayTreeDataParams) => {
  const displayTreeData = useMemo((): DisplayTreeObjectiveNode[] => {
    if (isEmpty(treeData)) {
      return treeData;
    }

    if (loadingMore) {
      return [
        ...treeData,
        {
          id: LOAD_MORE_OBJECTIVES_NODE_ID,
          name: "Loading...",
          active: false,
          toggled: false,
          children: null,
          noActivation: true,
        },
      ];
    }

    if (hasMore) {
      return [
        ...treeData,
        {
          id: LOAD_MORE_OBJECTIVES_NODE_ID,
          name: `Load more (${totalFetchedCount} of ${totalCount} loaded)`,
          active: false,
          toggled: false,
          isLoadMore: true,
          children: null,
        },
      ];
    }

    return treeData;
  }, [treeData, hasMore, loadingMore, totalFetchedCount, totalCount]);

  const onNodeSelected = useCallback(
    (node: DisplayTreeObjectiveNode) => {
      if (node.isLoadMore) {
        handleLoadMore({
          includeChildObjectivesParam: false,
        });
      }
    },
    [handleLoadMore]
  );

  return { displayTreeData, onNodeSelected };
};

export default useDisplayTreeData;
